import { Injectable, OnDestroy, inject } from '@angular/core';
import { EmbedInitPayload, EmbedSessionService } from './embed-session.service';

export const EMBED_PROTOCOL_VERSION = 1;

type HostToEmbedMessage =
  | {
      type: 'ai-cf:init';
      version?: number;
      token?: string;
      companyId?: string;
      apiUrl?: string;
      aiBaseUrl?: string;
      theme?: string;
      allowedOrigins?: string[];
    }
  | {
      type: 'ai-cf:update-auth';
      version?: number;
      token?: string;
      companyId?: string;
    };

@Injectable({ providedIn: 'root' })
export class EmbedBridgeService implements OnDestroy {
  private session = inject(EmbedSessionService);
  private bound = false;

  private readonly onMessage = (event: MessageEvent): void => {
    const data = event.data as HostToEmbedMessage | null;
    if (!data || typeof data !== 'object' || typeof data.type !== 'string') {
      return;
    }
    if (!data.type.startsWith('ai-cf:')) return;

    console.log('[ai-cf][embed] message received', {
      origin: event.origin,
      type: data.type,
      allowedOrigins: this.session.allowedOrigins(),
    });

    if (!this.session.isOriginAllowed(event.origin)) {
      console.warn('[ai-cf][embed] ignored message from disallowed origin', {
        origin: event.origin,
        allowedOrigins: this.session.allowedOrigins(),
      });
      return;
    }

    if (data.type === 'ai-cf:init') {
      const payload: EmbedInitPayload = {
        token: data.token,
        companyId: data.companyId,
        apiUrl: data.apiUrl,
        aiBaseUrl: data.aiBaseUrl,
        theme: data.theme,
        allowedOrigins: data.allowedOrigins,
      };
      this.session.applyInit(payload, event.origin);
      console.log('[ai-cf][embed] ai-cf:init applied', {
        hasAuth: this.session.hasAuth(),
        companyId: this.session.companyId(),
        tokenPresent: Boolean(this.session.token().trim()),
        tokenLength: this.session.token().length,
        apiUrl: this.session.apiUrl(),
        aiBaseUrl: this.session.aiBaseUrl(),
        hostOrigin: this.session.hostOrigin(),
      });
      return;
    }

    if (data.type === 'ai-cf:update-auth') {
      this.session.updateAuth(data.token, data.companyId);
      console.log('[ai-cf][embed] ai-cf:update-auth applied', {
        hasAuth: this.session.hasAuth(),
        companyId: this.session.companyId(),
        tokenPresent: Boolean(this.session.token().trim()),
        tokenLength: this.session.token().length,
      });
    }
  };

  start(): void {
    if (this.bound) return;
    window.addEventListener('message', this.onMessage);
    this.bound = true;
    const ready = { type: 'ai-cf:ready', version: EMBED_PROTOCOL_VERSION };
    console.log('[ai-cf][embed] posting ai-cf:ready to parent');
    this.postToParent(ready);
  }

  stop(): void {
    if (!this.bound) return;
    window.removeEventListener('message', this.onMessage);
    this.bound = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  notifyAuthExpired(message = 'Session expired'): void {
    this.postToParent({
      type: 'ai-cf:auth-expired',
      version: EMBED_PROTOCOL_VERSION,
      message,
    });
  }

  notifyApply(payload: {
    _id: string;
    programName?: string;
    institutionName?: string;
  }): void {
    this.postToParent({
      type: 'ai-cf:apply',
      version: EMBED_PROTOCOL_VERSION,
      _id: payload._id,
      programName: payload.programName,
      institutionName: payload.institutionName,
    });
  }

  notifyError(message: string): void {
    this.postToParent({
      type: 'ai-cf:error',
      version: EMBED_PROTOCOL_VERSION,
      message,
    });
  }

  private postToParent(payload: Record<string, unknown>): void {
    if (window.parent === window) {
      console.warn('[ai-cf][embed] not inside iframe — skip postToParent', payload);
      return;
    }
    const target = this.session.hostOrigin() || '*';
    console.log('[ai-cf][embed] postToParent', { target, type: payload['type'] });
    window.parent.postMessage(payload, target);
  }
}
